<template>
  <div>
    <v-btn-toggle
      :model-value="modelValue.method"
      color="primary"
      variant="outlined"
      density="comfortable"
      divided
      :disabled="disabled"
      class="mb-4 waste-wrap"
      @update:model-value="setMethod"
    >
      <v-btn value="gross_tare">
        {{ $t("acceptance.weighings.methods_short.gross_tare") }}
      </v-btn>
      <v-btn value="direct">
        {{ $t("acceptance.weighings.methods_short.direct") }}
      </v-btn>
    </v-btn-toggle>

    <v-row dense>
      <template v-if="modelValue.method === 'gross_tare'">
        <v-col cols="12" sm="4">
          <v-text-field
            :model-value="modelValue.gross"
            type="number"
            min="0"
            step="0.1"
            variant="outlined"
            density="comfortable"
            :label="`${$t('acceptance.weighings.dialog.gross')} *`"
            :error-messages="message('gross')"
            :disabled="disabled"
            @update:model-value="set('gross', $event)"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            :model-value="modelValue.tare"
            type="number"
            min="0"
            step="0.1"
            variant="outlined"
            density="comfortable"
            :label="`${$t('acceptance.weighings.dialog.tare')} *`"
            :hint="tareHint"
            persistent-hint
            :error-messages="message('tare')"
            :disabled="disabled"
            @update:model-value="set('tare', $event)"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <div class="text-caption text-grey-darken-1">
            {{ $t("acceptance.weighings.dialog.net_calculated") }}
          </div>
          <div class="text-h6">{{ netLabel }}</div>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            :model-value="modelValue.ticketNumber"
            variant="outlined"
            density="comfortable"
            maxlength="60"
            :label="$t('acceptance.weighings.dialog.ticket_number')"
            :error-messages="message('ticketNumber')"
            :disabled="disabled"
            @update:model-value="set('ticketNumber', $event)"
          />
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" sm="6">
          <v-text-field
            :model-value="modelValue.net"
            type="number"
            min="0"
            step="0.1"
            variant="outlined"
            density="comfortable"
            :label="`${$t('waste.weigh.net')} *`"
            :error-messages="message('net')"
            :disabled="disabled"
            @update:model-value="set('net', $event)"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            :model-value="modelValue.device"
            variant="outlined"
            density="comfortable"
            maxlength="120"
            :label="`${$t('acceptance.weighings.dialog.device')} *`"
            :error-messages="message('device')"
            :disabled="disabled"
            @update:model-value="set('device', $event)"
          />
        </v-col>
      </template>

      <v-col cols="12" sm="6">
        <v-text-field
          :model-value="modelValue.weighedAt"
          type="datetime-local"
          variant="outlined"
          density="comfortable"
          :max="maxWeighedAt"
          :label="`${$t('acceptance.weighings.dialog.weighed_at')} *`"
          :error-messages="message('weighedAt')"
          :disabled="disabled"
          @update:model-value="set('weighedAt', $event)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
// The weighing fields of the weigh and send-out dialogs (Stage 6 slice 3): the Stage 1 methods and
// rules (weighingForm.mjs), the tare filled in from the container. errors is { field: reason }
// (without the "weighing." prefix); an empty required field only disables Save.
import {
  MAX_KG,
  localDateTime,
  netPreview,
  switchMethod,
} from "@/components/Acceptance/weighingForm.mjs";
import { tareFromContainer } from "@/components/Waste/sendOutForm.mjs";

export default {
  name: "WasteContainerWeighingFields",
  props: {
    modelValue: { type: Object, required: true },
    container: { type: Object, default: null },
    errors: { type: Object, default: () => ({}) },
    // Server errors by field, shown until the field changes.
    serverErrors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    now: { type: Date, default: () => new Date() },
  },
  emits: ["update:modelValue"],
  computed: {
    netLabel() {
      const net = netPreview(this.modelValue);
      if (net === null) return "—";
      return `${this.formatNumber(net)} kg`;
    },
    maxWeighedAt() {
      return localDateTime(this.now);
    },
    tareHint() {
      return tareFromContainer(this.modelValue, this.container)
        ? this.$t("waste.weigh.tare_from_container")
        : "";
    },
  },
  methods: {
    set(field, value) {
      this.$emit("update:modelValue", { ...this.modelValue, [field]: value });
    },
    setMethod(method) {
      if (!method || method === this.modelValue.method) return;
      const next = switchMethod(this.modelValue, method);
      // Back to the weighbridge: the container's tare again.
      if (
        method === "gross_tare" &&
        this.container &&
        this.container.tare != null
      ) {
        next.tare = String(this.container.tare);
      }
      this.$emit("update:modelValue", next);
    },
    formatNumber(value) {
      return new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 1,
      }).format(value);
    },
    message(field) {
      if (this.serverErrors[field]) return [this.serverErrors[field]];
      const reason = this.errors[field];
      if (!reason || reason === "required") return [];
      if (reason === "weighing_stale") return [this.$t("waste.send_out.stale")];
      return [
        this.$t(`acceptance.weighings.errors.${reason}`, {
          max: this.formatNumber(MAX_KG),
        }),
      ];
    },
  },
};
</script>

<style scoped>
.waste-wrap {
  white-space: normal;
  flex-wrap: wrap;
  height: auto;
}
</style>
